/**
 * Normalizer for plan-card chat messages (`WORKOUT_CARD` / `DIET_CARD`).
 *
 * When a coach edits a client's workout or diet plan, the workouts/diets
 * services emit one event per change; ms-agents buffers them for 15 minutes and
 * collapses the burst into a single message posted into the client's COACHING
 * room, **authored as the coach**. The message's `content` is composed
 * server-side and is rendered verbatim — this module only unpacks the structured
 * plan data that rides alongside it.
 *
 * Two overlapping fields carry that data and either may be absent:
 *
 *  1. `content_attributes.plans[]` — **every** plan the message covers. This is
 *     the list to render.
 *  2. `plan_share` — the *primary* plan only (the backing table is
 *     `UNIQUE(message_id)`, so a message spanning two plans gets one row). Its
 *     value here is the tracked bits: the share id and its status, plus the
 *     scheduling window in `summary_snapshot`.
 *
 * Everything is pure so it can be unit-tested without mocking axios, and
 * `normalizePlanShare` **returns null rather than throwing**: realtime delivers
 * messages through `extractMessage`, which only validates `id` and an integer
 * `seq`, so a thin publish can arrive with `content_attributes` missing or
 * malformed. Null falls back to the plain text bubble — which still shows the
 * sentence. Throwing would take down the whole thread.
 */

/** Content types that render as a plan card. */
const PLAN_CARD_CONTENT_TYPES = ["WORKOUT_CARD", "DIET_CARD"];

/** `counts` keys → human label. Unknown keys fall back to the key itself. */
const COUNT_LABELS = {
  WORKOUT: "workout",
  MEAL: "meal",
  FOOD: "food item",
  EXERCISE: "exercise",
  SET: "set",
};

/**
 * @typedef {Object} PlanCount
 * @property {string} key - Raw counts key, e.g. "WORKOUT".
 * @property {string} label - Human label, e.g. "workout".
 * @property {number} added
 * @property {number} updated
 * @property {number} removed
 */

/**
 * @typedef {Object} PlanEntry
 * @property {string} id - Stable React key.
 * @property {'WORKOUT'|'DIET'|null} planKind
 * @property {string|null} planId
 * @property {string} title
 * @property {string|null} eventType - e.g. "ITEMS_ADDED".
 * @property {PlanCount[]} counts - Only entries with a non-zero movement.
 * @property {string|null} from - Schedule window start (primary plan only).
 * @property {string|null} to - Schedule window end (primary plan only).
 */

/**
 * @typedef {Object} PlanShare
 * @property {string|null} planShareId - `plan_share.id`, for the VIEWED receipt.
 * @property {string|null} status - SHARED | VIEWED | ACCEPTED | DECLINED | …
 * @property {'WORKOUT'|'DIET'|null} primaryKind
 * @property {PlanEntry[]} plans
 */

/**
 * Trimmed string, or "" for anything that isn't a non-empty string.
 * @param {*} value
 * @returns {string}
 */
const toText = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : "";

/**
 * Trimmed string, or null. Used where absence is meaningful.
 * @param {*} value
 * @returns {string|null}
 */
const toTextOrNull = (value) => toText(value) || null;

/**
 * Coerce to a non-negative integer; anything unusable becomes 0.
 * @param {*} value
 * @returns {number}
 */
const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/**
 * Plain object guard — arrays and null must not pass.
 * @param {*} value
 * @returns {boolean}
 */
const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * Normalize a `counts` map into render-ready rows.
 *
 * Entries where nothing moved are dropped: the server sends explicit zeroes
 * (`{ added: 0, updated: 0, removed: 0 }`) for untouched item kinds, and
 * rendering "0 workouts" next to a real change is noise.
 *
 * @param {*} counts - Expected `{ WORKOUT: { added, updated, removed } }`.
 * @returns {PlanCount[]}
 */
const normalizeCounts = (counts) => {
  if (!isPlainObject(counts)) return [];

  return Object.entries(counts)
    .map(([key, value]) => {
      if (!isPlainObject(value)) return null;
      const added = toCount(value.added);
      const updated = toCount(value.updated);
      const removed = toCount(value.removed);
      if (!added && !updated && !removed) return null;
      return {
        key,
        label: COUNT_LABELS[key] || String(key).toLowerCase(),
        added,
        updated,
        removed,
      };
    })
    .filter(Boolean);
};

/**
 * Normalize one `content_attributes.plans[]` entry.
 * @param {*} row
 * @param {number} index
 * @returns {PlanEntry|null}
 */
const normalizePlanRow = (row, index) => {
  if (!isPlainObject(row)) return null;

  const planId = toTextOrNull(row.plan_id);
  const planKind = toTextOrNull(row.plan_kind);
  const title = toText(row.title);
  const counts = normalizeCounts(row.counts);
  const eventType = toTextOrNull(row.event_type);

  // A row carrying no identity, no title and no movement describes nothing.
  if (!planId && !title && !counts.length && !eventType) return null;

  return {
    id: planId || `plan-${index}`,
    planKind,
    planId,
    title,
    eventType,
    counts,
    from: null,
    to: null,
  };
};

/**
 * Synthesize the single plan entry described by `plan_share`, used when
 * `content_attributes.plans` is absent.
 *
 * @param {Object} share
 * @returns {PlanEntry|null}
 */
const planFromShare = (share) => {
  const summary = isPlainObject(share.summary_snapshot)
    ? share.summary_snapshot
    : {};
  const planId = toTextOrNull(share.plan_id);
  const title = toText(share.title_snapshot);
  const counts = normalizeCounts(summary.counts);
  const eventType = toTextOrNull(summary.event_type);

  if (!planId && !title && !counts.length && !eventType) return null;

  return {
    id: planId || "plan-primary",
    planKind: toTextOrNull(share.plan_kind),
    planId,
    title,
    eventType,
    counts,
    from: toTextOrNull(summary.from),
    to: toTextOrNull(summary.to),
  };
};

/**
 * Unpack a plan-card message into a render-ready shape, or null when the
 * message isn't one (or carries nothing renderable).
 *
 * @param {Object|null|undefined} message - Raw server (or realtime) message.
 * @returns {PlanShare|null}
 */
export function normalizePlanShare(message) {
  if (!isPlainObject(message)) return null;
  if (!PLAN_CARD_CONTENT_TYPES.includes(message.content_type)) return null;

  const share = isPlainObject(message.plan_share) ? message.plan_share : null;
  const attributes = isPlainObject(message.content_attributes)
    ? message.content_attributes
    : null;

  const rows = Array.isArray(attributes?.plans) ? attributes.plans : [];
  let plans = rows.map(normalizePlanRow).filter(Boolean);

  // `plans` is authoritative for *which* plans; `plan_share` still owns the
  // scheduling window, so graft it onto whichever entry is the primary.
  if (plans.length && share) {
    const summary = isPlainObject(share.summary_snapshot)
      ? share.summary_snapshot
      : {};
    const from = toTextOrNull(summary.from);
    const to = toTextOrNull(summary.to);
    const primaryId = toTextOrNull(share.plan_id);
    if ((from || to) && primaryId) {
      plans = plans.map((plan) =>
        plan.planId === primaryId ? { ...plan, from, to } : plan
      );
    }
  }

  // No usable `plans` array — fall back to the primary described by plan_share.
  if (!plans.length && share) {
    const primary = planFromShare(share);
    if (primary) plans = [primary];
  }

  if (!plans.length) return null;

  return {
    planShareId: share ? toTextOrNull(share.id) : null,
    status: share ? toTextOrNull(share.status) : null,
    // Either field may be absent on a thin publish; the plans themselves are
    // the last word, so a card never loses its kind when it's plainly stated.
    primaryKind:
      toTextOrNull(attributes?.primary_plan_kind) ||
      (share ? toTextOrNull(share.plan_kind) : null) ||
      plans[0].planKind,
    plans,
  };
}
